import { PatternType, PatternRegistryOptions } from '../types/pattern.types';

class PatternRegistry {
  private patterns: Map<string, PatternType> = new Map();
  private orderedPatterns: PatternType[] = [];
  private config: PatternRegistryOptions;

  constructor(config: PatternRegistryOptions = {}) {
    this.config = {
      autoCompilePatterns: true,
      priorityMode: 'ordered',
      ...config
    };
  }

  register(pattern: PatternType): void {
    const compiledPattern = this.compilePattern(pattern);
    this.patterns.set(pattern.id, compiledPattern);
    this.rebuildOrderedList();
  }

  registerMany(patterns: PatternType[]): void {
    patterns.forEach(pattern => {
      const compiledPattern = this.compilePattern(pattern);
      this.patterns.set(pattern.id, compiledPattern);
    });
    this.rebuildOrderedList();
  }

  unregister(patternId: string): void {
    this.patterns.delete(patternId);
    this.rebuildOrderedList();
  }

  get(patternId: string): PatternType | undefined {
    return this.patterns.get(patternId);
  }

  getAll(): PatternType[] {
    return this.orderedPatterns;
  }

  getAllAsMap(): Map<string, PatternType> {
    return new Map(this.patterns);
  }

  has(patternId: string): boolean {
    return this.patterns.has(patternId);
  }

  clear(): void {
    this.patterns.clear();
    this.orderedPatterns = [];
  }

  private compilePattern(pattern: PatternType): PatternType {
    if (!this.config.autoCompilePatterns) {
      return pattern;
    }

    const result = { ...pattern };

    // Compile single pattern
    if (pattern.pattern && typeof pattern.pattern === 'string') {
      try {
        result.pattern = new RegExp(pattern.pattern);
      } catch (error) {
        // Failed to compile pattern
      }
    }

    // Compile patterns array
    if (pattern.patterns && Array.isArray(pattern.patterns)) {
      result.patterns = pattern.patterns.map(p => {
        if (typeof p === 'string') {
          try {
            return new RegExp(p);
          } catch (error) {
            // Failed to compile pattern
            return p;
          }
        }
        return p;
      });
    }

    return result;
  }

  private rebuildOrderedList(): void {
    const patternsArray = Array.from(this.patterns.values());

    if (this.config.priorityMode === 'explicit') {
      this.orderedPatterns = patternsArray.sort((a, b) => {
        const priorityA = a.priority ?? 0;
        const priorityB = b.priority ?? 0;
        return priorityB - priorityA;
      });
    } else {
      this.orderedPatterns = patternsArray;
    }
  }
}

const globalRegistry = new PatternRegistry();

export function getPatternRegistry(): PatternRegistry {
  return globalRegistry;
}

export function createPatternRegistry(config?: PatternRegistryOptions): PatternRegistry {
  return new PatternRegistry(config);
}

export function registerPattern(pattern: PatternType): void {
  globalRegistry.register(pattern);
}

export function registerPatterns(patterns: PatternType[]): void {
  globalRegistry.registerMany(patterns);
}

export function unregisterPattern(patternId: string): void {
  globalRegistry.unregister(patternId);
}

export function getPattern(patternId: string): PatternType | undefined {
  return globalRegistry.get(patternId);
}

export function getAllPatterns(): PatternType[] {
  return globalRegistry.getAll();
}

export function hasPattern(patternId: string): boolean {
  return globalRegistry.has(patternId);
}

export function clearPatterns(): void {
  globalRegistry.clear();
}

export interface SeedValidationResult {
  isValid: boolean;
  error?: string;
}

export interface SeedFilterDefinition {
  id: string;
  label: string;
  description: string;
  inputType: 'text' | 'date' | 'select' | 'iso2';
  validation?: {
    minLength?: number;
    maxLength?: number;
    format?: string;
  };
  options?: { value: string; label: string }[];
}

export function getSeedFilters(patternId: string): SeedFilterDefinition[] | undefined {
  const pattern = globalRegistry.get(patternId);
  if (!pattern?.metadata?.filters) {
    return undefined;
  }
  return pattern.metadata.filters as SeedFilterDefinition[];
}

export function hasSeedFilters(patternId: string): boolean {
  const filters = getSeedFilters(patternId);
  return !!filters && filters.length > 0;
}

export function validateSeedValue(value: string, patternId: string): SeedValidationResult {
  const pattern = globalRegistry.get(patternId);
  
  if (!pattern) {
    return { isValid: false, error: 'Unknown seed type' };
  }

  if (!value || value.trim() === '') {
    return { isValid: false, error: 'Value is required' };
  }

  const trimmedValue = value.trim();
  const label = pattern.label || pattern.id;

  // Get error message from metadata if available (backend-driven)
  const errorMessage = pattern.metadata?.errorMessage as string | undefined;

  // Collect all patterns to test
  const patternsToTest: (RegExp | string)[] = [];
  
  if (pattern.pattern) {
    patternsToTest.push(pattern.pattern);
  }
  if (pattern.patterns && Array.isArray(pattern.patterns)) {
    patternsToTest.push(...pattern.patterns);
  }

  // If no patterns defined, accept any value
  if (patternsToTest.length === 0) {
    return { isValid: true };
  }

  // Test against patterns
  const matchesPattern = patternsToTest.some(p => {
    if (p instanceof RegExp) {
      return p.test(trimmedValue);
    }
    if (typeof p === 'string') {
      try {
        return new RegExp(p).test(trimmedValue);
      } catch {
        return false;
      }
    }
    return false;
  });

  if (!matchesPattern) {
    // Use backend error message if available, otherwise fall back to generic
    const error = errorMessage || `Invalid ${label} format`;
    return { isValid: false, error };
  }

  return { isValid: true };
}
